import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { orderReportSortings } from './constants';
import { emptyObject } from 'ui-components/default-props/empty';
import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import {
  useOrderReportSorting,
  useHideOrderReportImages,
  useSimpleOrderSummary,
  useOrderSummaryMasterSku,
  useOrderSummaryScanToViewBarcode,
} from '../../../../../hooks/use-seller-setting';
import { findMatchingSettingEnum } from '../helpers';
import messages from './messages';
import sharedMessages from '../shared-messages';
import sharedStyle from '../style.module.less';

const OrderSummaryDocumentOptionsModal = ({ settingsState = emptyObject, onDropdownChange, onCheckboxChange, onInit }) => {
  const intl = useIntl();
  const orderReportSortingSetting = useOrderReportSorting();
  const hideOrderReportImagesSetting = useHideOrderReportImages();
  const simpleOrderSummarySetting = useSimpleOrderSummary();
  const orderSummaryMasterSkuSetting = useOrderSummaryMasterSku();
  const orderSummaryScanToViewBarcodeSetting = useOrderSummaryScanToViewBarcode();
  const initialSettingsState = useMemo(
    () => ({
      [SellerSettings.orderReportSorting.key]: orderReportSortingSetting,
      [SellerSettings.hideOrderReportImages.key]: hideOrderReportImagesSetting,
      [SellerSettings.simpleOrderSummary.key]: simpleOrderSummarySetting,
      [SellerSettings.orderSummaryMasterSku.key]: orderSummaryMasterSkuSetting,
      [SellerSettings.orderSummaryScanToViewBarcode.key]: orderSummaryScanToViewBarcodeSetting,
    }),
    [
      orderReportSortingSetting,
      hideOrderReportImagesSetting,
      simpleOrderSummarySetting,
      orderSummaryMasterSkuSetting,
      orderSummaryScanToViewBarcodeSetting,
    ],
  );

  useEffect(() => {
    onInit(initialSettingsState);
  }, [onInit, initialSettingsState]);

  const orderReportSorting =
    settingsState[SellerSettings.orderReportSorting.key] || initialSettingsState[SellerSettings.orderReportSorting.key];
  const hideImages =
    settingsState[SellerSettings.hideOrderReportImages.key] ?? initialSettingsState[SellerSettings.hideOrderReportImages.key];
  const useProductSku =
    settingsState[SellerSettings.orderSummaryMasterSku.key] ?? initialSettingsState[SellerSettings.orderSummaryMasterSku.key];
  const simpleOrderSummary =
    settingsState[SellerSettings.simpleOrderSummary.key] ?? initialSettingsState[SellerSettings.simpleOrderSummary.key];
  const showScanToViewBarcode =
    settingsState[SellerSettings.orderSummaryScanToViewBarcode.key] ??
    initialSettingsState[SellerSettings.orderSummaryScanToViewBarcode.key];
  return (
    <>
      <BannerMessageV2 content={<FormattedMessage {...sharedMessages.affectsAllUsersWarning} />} status={BannerStatus.Info} />
      <div className={sharedStyle.description}>
        <FormattedMessage {...messages.description} />
      </div>
      <div className={sharedStyle['print-options-grid']}>
        <FormattedMessage {...messages.sortOrdersBy} />
        <div>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth id={SellerSettings.orderReportSorting.key} variant='form'>
            <DropdownMenuToggler
              className={sharedStyle['dropdown-toggler']}
              title={intl.formatMessage(findMatchingSettingEnum(orderReportSortings, orderReportSorting).messageDescriptor)}
            >
              {intl.formatMessage(findMatchingSettingEnum(orderReportSortings, orderReportSorting).messageDescriptor)}
            </DropdownMenuToggler>
            {orderReportSortings.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem key={value} onClick={() => onDropdownChange(SellerSettings.orderReportSorting.key, value)}>
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
        <FormattedMessage {...messages.productImages} />
        <div>
          <Checkbox
            checked={hideImages}
            label={<FormattedMessage {...messages.hideImages} />}
            name={SellerSettings.hideOrderReportImages.key}
            onChange={onCheckboxChange}
          />
        </div>
        <FormattedMessage {...messages.productSkuLabel} />
        <div>
          <Checkbox
            checked={useProductSku}
            label={<FormattedMessage {...messages.useProductSku} />}
            name={SellerSettings.orderSummaryMasterSku.key}
            onChange={onCheckboxChange}
          />
        </div>
        <FormattedMessage {...messages.simpleSummaryLabel} />
        <div>
          <Checkbox
            checked={simpleOrderSummary}
            label={<FormattedMessage {...messages.simpleSummaryOption} />}
            name={SellerSettings.simpleOrderSummary.key}
            onChange={onCheckboxChange}
          />
        </div>
        <FormattedMessage {...messages.showScanToViewBarcodeLabel} />
        <div>
          <Checkbox
            checked={showScanToViewBarcode}
            label={<FormattedMessage {...messages.showScanToViewBarcodeOption} />}
            name={SellerSettings.orderSummaryScanToViewBarcode.key}
            onChange={onCheckboxChange}
          />
        </div>
      </div>
    </>
  );
};

OrderSummaryDocumentOptionsModal.propTypes = {
  onInit: PropTypes.func.isRequired,
  onDropdownChange: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  settingsState: PropTypes.object,
};

export default OrderSummaryDocumentOptionsModal;
